import {NumberOutput, MaterialReference} from "@cm/template-nodes"
import type {NumberLike} from "@cm/template-nodes"
import type {MattressCurveLikeNode} from "#mattress/role-markers"
import type {MattressFabricLayout} from "#mattress/nodes/mattress-cut"
import type {MattressComponentGraphNode} from "#mattress/nodes/mattress-nodes"
import {MattressCurveOutput} from "#mattress/nodes/curve/mattress-curve-output"
import {MattressCurve} from "#mattress/nodes/curve/mattress-curve"
import {createNumberInput, createMaterialInput, createMaterialReference} from "#mattress/assets-definitions/common"
import {
    createMattressCurve,
    createMattressReferenceFrame,
    createMattressCut,
    createMattressComponentGraph,
    createMattressComponentInstance,
    createMattressGroup,
} from "#mattress/assets-definitions/create-mattress"
import {uiPathMetaData, createPath, panelPathName, bottomPanelPathName, topPanelPathName} from "#mattress/mattress-meta-data"
import {getComponentGraphForAccessoryStack} from "#mattress/assets-definitions/standard-models/common/accessory-stack"
import {getComponentGraphNodesForFabricStack} from "#mattress/assets-definitions/standard-models/common/fabric-stack"
import {
    topMirrorControlPoints3cm,
    bottomMirrorControlPoints3cm,
    bottomEdgeControlPoints,
} from "#mattress/assets-definitions/standard-models/common/uv-constants"
import {
    getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate,
    getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate,
} from "#mattress/assets-definitions/standard-models/common/material-offset"
import type {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"

export function getComponentGraphNodesForBottomPart(inputData: {
    bottomHeight: NumberLike
    width: NumberLike
    length: NumberLike
    safeBottomRadius: NumberLike
    openingOnBottom: boolean
    defaultMaterialLegacyId?: number
    fabricLayout?: MattressFabricLayout
    bottomPanelMode?: "edge" | "mirror"
}) {
    const {bottomHeight, openingOnBottom, defaultMaterialLegacyId, fabricLayout = "InvertedWaterfall", bottomPanelMode} = inputData

    const bottomHeightReferenceFrame = createMattressReferenceFrame("Bottom Height Reference Frame", "zAxisRing", "global", bottomHeight, 1)
    const bottomHeightCurve = createMattressCurve("Bottom Height Curve", 0, bottomHeightReferenceFrame, 6)

    const {nodes: accessoriesNodes} = getComponentGraphForAccessoryStack(1)
    const accessoriesGraph = createMattressComponentGraph("Accessories", accessoriesNodes)
    const bottomCurveAccessories = createMattressComponentInstance("Accessories Bottom Curve", "accessories-bottom-curve", accessoriesGraph, {
        "ref-curve": bottomHeightCurve,
    })
    const bottomPartMaterial = createMaterialInput(
        "Bottom Part Material",
        "bottom-part-material",
        uiPathMetaData([createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 0),
        defaultMaterialLegacyId
            ? new MaterialReference({
                  parameters: {
                      name: "Default Bottom Part Material",
                      materialId: defaultMaterialLegacyId,
                      materialRevisionId: null,
                  },
              })
            : undefined,
    )

    const topAndBottomPartMaterialOffsetComponentGraph = getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate()
    const {materialOffsetComponentInstance: bottomPartMaterialOffsetInstance, materialOffsetOutput: bottomPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Bottom Part Material Offset",
            componentInstanceId: "bottom-part-material-offset",
            outputName: "Bottom Part Material Offset",
            material: bottomPartMaterial,
            width: inputData.width,
            length: inputData.length,
            heigth: bottomHeight,
            edgeRadius: inputData.safeBottomRadius,
        })

    const bottomPartCut = createMattressCut(
        openingOnBottom ? "Border" : "Bottom Panel",
        [{curve: bottomHeightCurve, side: "right"}],
        fabricLayout,
        bottomPartMaterialOffset,
    )
    const bottomPartGroup = createMattressGroup("Bottom Part", [
        bottomHeightReferenceFrame,
        bottomHeightCurve,
        topAndBottomPartMaterialOffsetComponentGraph,
        bottomPartMaterial,
        bottomPartMaterialOffsetInstance,
        bottomPartMaterialOffset,
        bottomPartCut,
        bottomCurveAccessories,
        accessoriesGraph,
    ])

    if (bottomPanelMode) {
        const {nodes: bottomPanelNodes} = getComponentGraphNodesForBottomPanel({
            defaultBottomPanelMaterialLegacyId: defaultMaterialLegacyId,
            mode: bottomPanelMode,
            width: inputData.width,
            length: inputData.length,
            topAndBottomPartMaterialOffsetComponentGraph,
        })
        bottomPartGroup.parameters.nodes.addEntry(createMattressGroup("Bottom Panel", bottomPanelNodes))
    }

    return {bottomHeightCurve, bottomPartGroup, topAndBottomPartMaterialOffsetComponentGraph}
}

export function getComponentGraphNodesForMiddlePart(inputData: {
    bottomHeight: NumberLike
    bottomHeightCurve: MattressCurveLikeNode
    layerReferenceFrameUnit?: NumberLike
    defaultMaterialLegacyId?: number
    defaultLayerHeight?: number
    defaultLayerCount?: number
    fabricLayout?: MattressFabricLayout
}) {
    const {bottomHeight, bottomHeightCurve, layerReferenceFrameUnit, defaultMaterialLegacyId, defaultLayerHeight, defaultLayerCount, fabricLayout} = inputData

    const layerCount = createNumberInput("Layer Count", "layer-count", defaultLayerCount ?? 1)
    const {nodes: fabricStackNodes, stackInstance: fabricStackInstance} = getComponentGraphNodesForFabricStack({
        bottomOffset: bottomHeight,
        bottomCurve: bottomHeightCurve,
        layerReferenceFrameUnit,
        layerCount,
        defaultMaterialLegacyId,
        defaultLayerHeight,
        fabricLayout,
    })
    const middlePartTopLayerMiddleCurve = fabricStackInstance.createOutput("middle-curve", {
        source: "component",
        outputNodeClass: MattressCurveOutput,
        name: "Middle Part Top Layer Middle Curve",
    })
    if (!(middlePartTopLayerMiddleCurve instanceof MattressCurveOutput))
        throw new Error("Middle part top layer middle curve output must be a MattressCurveOutput")
    const middlePartTopOffset = fabricStackInstance.createOutput("top-offset", {
        source: "template",
        outputNodeClass: NumberOutput,
        name: "Middle Part Top Offset",
    })
    if (!(middlePartTopOffset instanceof NumberOutput)) throw new Error("Fabric stack top offset output must be a NumberOutput")
    const middlePartTopCurve = fabricStackInstance.createOutput("top-curve", {
        source: "component",
        outputNodeClass: MattressCurveOutput,
        name: "Middle Part Top Curve",
    })
    if (!(middlePartTopCurve instanceof MattressCurveOutput)) throw new Error("Middle part top curve output must be a MattressCurveOutput")

    const middlePartGroup = createMattressGroup("Middle Part", [
        layerCount,
        ...fabricStackNodes,
        middlePartTopOffset,
        middlePartTopCurve,
        middlePartTopLayerMiddleCurve,
    ])

    return {middlePartGroup, middlePartTopOffset, middlePartTopCurve, middlePartTopLayerMiddleCurve, middlePartFabricLayersCount: layerCount}
}

export function getComponentGraphNodesForTopPart(inputData: {
    topHeight: NumberLike
    width: NumberLike
    length: NumberLike
    safeTopRadius: NumberLike
    middlePartTopCurve: MattressCurveLikeNode
    defaultMaterialLegacyId?: number
    fabricLayout?: MattressFabricLayout
    topAndBottomPartMaterialOffsetComponentGraph: MattressComponentGraph
}) {
    const {middlePartTopCurve, defaultMaterialLegacyId, topAndBottomPartMaterialOffsetComponentGraph, fabricLayout = "Waterfall"} = inputData

    const topPartMaterial = createMaterialInput(
        "Top Part Material",
        "top-part-material",
        uiPathMetaData([createPath(panelPathName, 0, createPath(topPanelPathName, 0))], 0),
        defaultMaterialLegacyId
            ? new MaterialReference({
                  parameters: {
                      name: "Default Top Part Material",
                      materialId: defaultMaterialLegacyId,
                      materialRevisionId: null,
                  },
              })
            : undefined,
    )

    const {materialOffsetComponentInstance: topPartMaterialOffsetInstance, materialOffsetOutput: topPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Top Part Material Offset",
            componentInstanceId: "top-part-material-offset",
            outputName: "Top Part Material Offset",
            material: topPartMaterial,
            width: inputData.width,
            length: inputData.length,
            heigth: inputData.topHeight,
            edgeRadius: inputData.safeTopRadius,
        })

    const topPartCut = createMattressCut("Top Panel", [{curve: middlePartTopCurve, side: "left"}], fabricLayout, topPartMaterialOffset)
    const topPartGroup = createMattressGroup("Top Part", [topPartMaterial, topPartMaterialOffsetInstance, topPartMaterialOffset, topPartCut])

    return {topPartGroup}
}

export function getComponentGraphNodesForTopPanel(inputData: {
    defaultTopPanelMaterialLegacyId?: number
    mode: "mirror"
    width: NumberLike
    length: NumberLike
    topAndBottomPartMaterialOffsetComponentGraph: MattressComponentGraph
}) {
    const {defaultTopPanelMaterialLegacyId, width, length, topAndBottomPartMaterialOffsetComponentGraph} = inputData

    const curve = new MattressCurve({
        parameters: {
            name: "Top Panel Curve",
            closed: true,
            resolution: 0.5,
            controlPoints: topMirrorControlPoints3cm,
        },
    })
    const material = createMaterialInput(
        "Top Panel Material",
        "top-panel-material",
        undefined,
        defaultTopPanelMaterialLegacyId ? createMaterialReference(defaultTopPanelMaterialLegacyId) : undefined,
    )

    const {materialOffsetComponentInstance: topPartMaterialOffsetInstance, materialOffsetOutput: topPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Top Panel Material Offset",
            componentInstanceId: "top-panel-material-offset",
            outputName: "Top Panel Material Offset",
            material,
            width,
            length,
            heigth: 0,
            edgeRadius: 0,
        })

    const cut = createMattressCut("Top Panel", [{curve, side: "right"}], "Waterfall", topPartMaterialOffset)

    const {nodes: accessoriesNodes} = getComponentGraphForAccessoryStack(3)
    const accessoriesStackGraph = createMattressComponentGraph("Accessories Graph", accessoriesNodes)
    const topPanelAccessories = createMattressComponentInstance("Accessories Top Panel", "accessories-top-panel", accessoriesStackGraph, {
        "ref-curve": curve,
    })

    return {
        nodes: [curve, material, topPartMaterialOffsetInstance, topPartMaterialOffset, cut, accessoriesStackGraph, topPanelAccessories],
        curve,
    }
}

export function getComponentGraphNodesForBottomPanel(inputData: {
    defaultBottomPanelMaterialLegacyId?: number
    mode: "mirror" | "edge"
    width: NumberLike
    length: NumberLike
    topAndBottomPartMaterialOffsetComponentGraph: MattressComponentGraph
    fabricLayout?: MattressFabricLayout
}) {
    const {defaultBottomPanelMaterialLegacyId, width, length, topAndBottomPartMaterialOffsetComponentGraph, fabricLayout = "InvertedWaterfall"} = inputData

    const curve = new MattressCurve({
        parameters: {
            name: "Bottom Panel Curve",
            closed: true,
            resolution: 0.5,
            controlPoints: inputData.mode === "mirror" ? bottomMirrorControlPoints3cm : bottomEdgeControlPoints,
        },
    })
    const material = createMaterialInput(
        "Bottom Panel Material",
        "bottom-panel-material",
        undefined,
        defaultBottomPanelMaterialLegacyId ? createMaterialReference(defaultBottomPanelMaterialLegacyId) : undefined,
    )

    const {materialOffsetComponentInstance: bottomPartMaterialOffsetInstance, materialOffsetOutput: bottomPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Bottom Panel Material Offset",
            componentInstanceId: "bottom-panel-material-offset",
            outputName: "Bottom Panel Material Offset",
            material,
            width,
            length,
            heigth: 0,
            edgeRadius: 0,
        })
    const cut = createMattressCut("Bottom Panel", [{curve, side: "left"}], fabricLayout, bottomPartMaterialOffset)
    const {nodes: accessoriesNodes} = getComponentGraphForAccessoryStack(2)
    const accessoriesStackGraph = createMattressComponentGraph("Accessories Graph", accessoriesNodes)
    const bottomPanelAccessories = createMattressComponentInstance("Accessories Bottom Panel", "accessories-bottom-panel", accessoriesStackGraph, {
        "ref-curve": curve,
    })

    return {
        nodes: [curve, material, bottomPartMaterialOffsetInstance, bottomPartMaterialOffset, cut, accessoriesStackGraph, bottomPanelAccessories],
        curve,
    }
}

export function getComponentGraphNodesForNonStackableMiddlePart(inputData: {
    bottomPanelCurve: MattressCurveLikeNode
    topPanelCurve: MattressCurveLikeNode
    defaultMaterialLegacyId?: number
    fabricLayout?: MattressFabricLayout
}) {
    const {bottomPanelCurve, topPanelCurve, defaultMaterialLegacyId, fabricLayout = "Border"} = inputData

    const material = createMaterialInput(
        "Middle Part Material",
        "middle-part-material",
        uiPathMetaData([createPath(panelPathName, 1)], 0),
        defaultMaterialLegacyId ? createMaterialReference(defaultMaterialLegacyId) : undefined,
    )
    const cut = createMattressCut(
        "Border",
        [
            {curve: bottomPanelCurve, side: "right"},
            {curve: topPanelCurve, side: "left"},
        ],
        fabricLayout,
        material,
    )

    const nodes: MattressComponentGraphNode[] = [material, cut]
    return {nodes}
}
